import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { DecimalFormatterDirective } from 'shared/directives/decimal-formatter.directive';
import { QuantityDirective } from 'shared/directives/quantity.directive';
import { SelectDropdownComponent } from 'shared/components/select-dropdown/select-dropdown.component';
import { Product, Utils, DbProduct, VatRateUtils } from 'fakturnia-shared';
import { ProductsService } from 'shared/services/products.service';

@Component({
  standalone: true,
  selector: 'app-order-products-table',
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    MatAutocompleteModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    DecimalFormatterDirective,
    QuantityDirective,
    SelectDropdownComponent
  ],
  templateUrl: './order-products-table.component.html',
  styleUrls: ['./order-products-table.component.scss']
})
export class OrderProductsTableComponent {

  /** Lista produktów do wyświetlenia */
  @Input() products: Product[] = [];

  /** Nagłówki kolumn tabeli (document.table.viewHeaders) */
  @Input() viewHeaders: any[] = [];

  /** Tryb tylko do odczytu */
  @Input() readonly = false;

  /** Czy wiersze mają efekt striped */
  @Input() striped = true;

  /** Dostępne stawki VAT */
  @Input() vatRates: any[] = [];

  /** Dostępne jednostki miary */
  @Input() measurementUnits: any[] = [];

  /** Kody GTU */
  @Input() gtu: any[] = [];

  /** Czy faktura ma włączone GTU */
  @Input() hasGTU = false;

  /** Czy faktura ma włączone PKWiU */
  @Input() hasPKWiU = false;

  /** Czy faktura ma włączony rabat */
  @Input() hasDiscount = false;

  /** Waluta */
  @Input() currency = 'PLN';

  /** Czy wyświetlać przycisk "Dodaj produkt" */
  @Input() showAddButton = true;

  /** Minimalna liczba produktów (jeśli > 0, nie pozwala usunąć ostatniego) */
  @Input() minProducts = 1;

  /** Emitowane przy zmianie produktu */
  @Output() productChange = new EventEmitter<void>();

  /** Emitowane przy kliknięciu "Dodaj produkt" */
  @Output() productAdd = new EventEmitter<void>();

  /** Emitowane przy usunięciu produktu (index) */
  @Output() productRemove = new EventEmitter<number>();

  /** Emitowane przy zapisie produktu */
  @Output() productSave = new EventEmitter<Product>();

  /** Wyniki wyszukiwania produktów */
  foundProducts: any[] = [];

  constructor(private _productsService: ProductsService) {}

  /** Wyszukiwanie produktów po nazwie */
  findProducts(productName: string): void {
    if (!productName) {
      this.foundProducts = [];
      return;
    }
    productName = productName.toLowerCase();

    const paginatedResults = this._productsService.browseProducts({
      currentPage: 1,
      pageSize: 20,
      filters: [
        (product: DbProduct) => product.isDeleted == false
      ],
      activeFilters: [
        ['searchText', productName]
      ]
    });

    this.foundProducts = paginatedResults.results || [];
  }

  /** Obsługa wyboru produktu z autocomplete */
  onOptionSelected(event: any, product: Product): void {
    const selectedProduct = event.option.value;
    product.update(selectedProduct);
    this.foundProducts = [];
    this.productChange.emit();
  }

  /** Funkcja wyświetlania nazwy w autocomplete */
  displayProductNameFn(product: Product): string {
    return product && product.name ? product.name : '';
  }

  /** Formatowanie wartości do formatu z 2 miejscami po przecinku */
  toDecimal(value: any): string {
    return Utils.toDecimal(value);
  }

  /** Sprawdza czy grossValue jest liczbą */
  isNumberGrossValue(value: any): boolean {
    return typeof value === 'number';
  }

  /** Pobiera etykietę stawki VAT */
  getVatRateLabel(vatRate: string | number): string {
    return VatRateUtils.getDisplayValue(vatRate);
  }

  /** Obsługa zmiany produktu - emituje event do rodzica */
  onProductChange(): void {
    this.productChange.emit();
  }

  /** Obsługa kliknięcia "Dodaj produkt" */
  onAddProduct(): void {
    this.productAdd.emit();
  }

  /** Obsługa usunięcia produktu */
  onRemoveProduct(index: number): void {
    this.productRemove.emit(index);
  }

  dismissedSavePrompts = new Set<Product>()

  /** Ukryj komunikat 'Czy chcesz zapisać' */
  onDismissSaveProduct(product: Product): void {
    this.dismissedSavePrompts.add(product)
  }

  /** Obsługa zapisu produktu */
  onSaveProduct(product: Product): void {
    this.productSave.emit(product);
  }
}
